/** Quality Report (PRD §31.1). The reviewer is a gate, not the truth — the owner's review stays mandatory. */
import type { QualityReport } from "@contentops/contract";
import { Icon } from "../../components/Icon";
import { Badge, Card } from "../../components/ui";
import { clock, parseClock } from "../../lib/format";

export function QualityReportCard({ report, onSeek }: { report: QualityReport; onSeek: (seconds: number) => void }) {
  const r = report.reviewer;
  const scoreTone = r.median >= 9 ? "ok" : r.median >= 8 ? "attention" : "danger";
  const worstAt = r.worst_moment ? parseClock(r.worst_moment.t) : null;
  const dims = Object.entries(r.dimensions);
  const o = report.output;
  return (
    <Card title="Quality report" className="qr">
      <div className="qr-score">
        <span className={`qr-number tone-${scoreTone}`}>{r.median.toFixed(1)}</span>
        <span className="qr-score-meta">
          <span>Reviewer median{r.spread != null ? <> · spread ± {r.spread.toFixed(2)}</> : null}</span>
          <span className="muted small">Gate: ≥ 9.0 · median of 3</span>
        </span>
      </div>
      {r.worst_moment && (
        <button type="button" className="qr-worst" onClick={() => worstAt != null && onSeek(worstAt)} disabled={worstAt == null} aria-label={`Weakest moment at ${r.worst_moment.t}: ${r.worst_moment.why}. Play from there.`}>
          <span className="qr-worst-t"><Icon name="play" size={16} /> {worstAt != null ? clock(worstAt) : r.worst_moment.t}</span>
          <span><b>Weakest moment.</b> {r.worst_moment.why}</span>
        </button>
      )}
      {r.blocking_defects.length > 0 && <div className="qr-defects"><Badge tone="danger">Blocking</Badge><ul>{r.blocking_defects.map((d) => <li key={d}>{d}</li>)}</ul></div>}
      {dims.length > 0 && (
        <ul className="qr-dims" aria-label="Scores by dimension">
          {dims.map(([k, v]) => (
            <li key={k}><span>{k.replace(/_/g, " ")}</span><span className="qr-bar"><span style={{ width: `${Math.max(0, Math.min(10, v)) * 10}%` }} /></span><b>{v}</b></li>
          ))}
        </ul>
      )}
      <dl className="qr-grid">
        <div><dt>Sign-off</dt><dd>{report.signoff.cleared ? <Badge tone="ok">✓ {report.signoff.items} items cleared</Badge> : <Badge tone="danger">Not cleared</Badge>}</dd></div>
        <div><dt>Output</dt><dd>{o.passed ? <Badge tone="ok">✓ passed</Badge> : <Badge tone="danger">failed</Badge>} <span className="muted small">{clock(o.duration_s)}</span></dd></div>
        <div><dt>Loudness</dt><dd>{o.lufs != null ? `${o.lufs.toFixed(1)} LUFS` : "–"}{o.true_peak_db != null ? ` · ${o.true_peak_db.toFixed(1)} dBTP` : ""}</dd></div>
        {report.sound && <div><dt>Sound</dt><dd>{[report.sound.cue_audit && `cues ${report.sound.cue_audit}`, report.sound.guard && `guard ${report.sound.guard}`, report.sound.seams && `seams ${report.sound.seams}`].filter(Boolean).join(" · ")}</dd></div>}
        {report.facts && <div><dt>Facts</dt><dd>{report.facts}</dd></div>}
        {report.usage && <div><dt>Cost &amp; time</dt><dd>{[report.usage.wall_clock_min != null && `${report.usage.wall_clock_min} min`, report.usage.services_usd != null && `$${report.usage.services_usd.toFixed(2)}`, report.usage.subscription_window_pct != null && `${report.usage.subscription_window_pct} % of window`].filter(Boolean).join(" · ")}</dd></div>}
      </dl>
      {report.names_spoken.length > 0 && (
        <div className="qr-names">
          <h3 className="qr-sub">Names spoken</h3>
          <ul>{report.names_spoken.map((n) => (
            <li key={n.name} className={n.confirmed ? "ok-text" : "attention-text"}>
              <span aria-hidden>{n.confirmed ? "✓" : "?"}</span> {n.name}<span className="sr-only">{n.confirmed ? " — confirmed" : " — not yet confirmed"}</span>
            </li>
          ))}</ul>
        </div>
      )}
      {report.held_items.length > 0 && <div className="qr-held"><h3 className="qr-sub">Held items</h3><ul>{report.held_items.map((h) => <li key={h}>{h}</li>)}</ul></div>}
    </Card>
  );
}
